import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { Subscription } from 'rxjs';

import type { MultiCurrencyMode } from '@core/models/kiosk-global-settings.model';
import { KioskGlobalSettingsService } from '@store/kiosk-global-settings/kiosk-global-settings.service';

import { sortCurrencyRates } from './convert-display-currency.util';
import { CurrencyRatesSourceRegistry } from './currency-rates-source.registry';
import type { CurrencyRate } from './currency-rate.model';

/**
 * In-memory display currency for the kiosk. Nothing is persisted: a reload or
 * `resetSelection()` returns to the marketplace currency.
 */
@Injectable({
  providedIn: 'root',
})
export class DisplayCurrencyService {
  private readonly kioskSettings = inject(KioskGlobalSettingsService);
  private readonly registry = inject(CurrencyRatesSourceRegistry);

  private readonly modeState = signal<MultiCurrencyMode>('off');
  private readonly ratesState = signal<CurrencyRate[]>([]);
  private readonly selectedCodeState = signal<string | null>(null);
  private readonly loadingState = signal(false);
  private readonly loadErrorState = signal(false);

  private appliedMode: MultiCurrencyMode | null = null;
  private requestId = 0;
  private loadSubscription?: Subscription;

  readonly mode = this.modeState.asReadonly();
  readonly isEnabled = computed(() => this.modeState() !== 'off');
  readonly rates = this.ratesState.asReadonly();
  readonly orderedRates = computed(() => sortCurrencyRates(this.ratesState()));
  readonly loading = this.loadingState.asReadonly();
  readonly loadError = this.loadErrorState.asReadonly();
  readonly selectedRate = computed(() => {
    const code = this.selectedCodeState();
    if (!code) {
      return null;
    }
    return this.ratesState().find(rate => rate.code === code) ?? null;
  });
  readonly ratesValidFrom = computed(() => this.orderedRates()[0]?.validFrom ?? null);

  private readonly syncModeEffect = effect(() => {
    const mode = this.kioskSettings.settings().multiCurrency?.mode ?? 'off';
    untracked(() => this.applyMode(mode));
  });

  constructor() {
    void this.syncModeEffect;
  }

  select(code: string | null): void {
    if (!code) {
      this.selectedCodeState.set(null);
      return;
    }
    const match = this.ratesState().find(rate => rate.code === code.toUpperCase());
    this.selectedCodeState.set(match?.code ?? null);
  }

  resetSelection(): void {
    this.selectedCodeState.set(null);
  }

  private applyMode(mode: MultiCurrencyMode): void {
    if (this.appliedMode === mode) {
      return;
    }
    this.appliedMode = mode;
    this.modeState.set(mode);
    this.selectedCodeState.set(null);
    this.fetch(mode);
  }

  private fetch(mode: MultiCurrencyMode): void {
    this.requestId += 1;
    const requestId = this.requestId;
    this.loadSubscription?.unsubscribe();

    if (mode === 'off') {
      this.ratesState.set([]);
      this.loadingState.set(false);
      this.loadErrorState.set(false);
      return;
    }

    this.loadingState.set(true);
    this.loadErrorState.set(false);
    this.loadSubscription = this.registry
      .get(mode)
      .loadRates()
      .subscribe({
        next: rates => {
          if (requestId !== this.requestId) {
            return;
          }
          this.ratesState.set(rates);
          this.loadingState.set(false);
        },
        error: () => {
          if (requestId !== this.requestId) {
            return;
          }
          this.ratesState.set([]);
          this.selectedCodeState.set(null);
          this.loadingState.set(false);
          this.loadErrorState.set(true);
        },
      });
  }
}
